/**
 * Benefit definitions for Dental HMO (DHMO)
 *
 * DHMO uses a fundamentally different structure than Dental PPO:
 * - Each ADA code is a simple dollar amount field (member cost)
 * - No network splits (IN/OUT) - all fields are networkDependent: false
 * - No service categories, deductibles, or coinsurance percentages
 */

import {
  BaseBenefitPlanPolicyPeriod,
  EmployerContributionEnum,
  extendEnumWithNotDisclosed,
  extendEnumWithNotDisclosedAndNotOffered,
  FundingTypeEnum,
  RateGuaranteeTypeEnum,
  RfpAnnualOpenEnrollmentEnum,
} from "@platform/openapi-types";
import { ICellEditorParams } from "ag-grid-enterprise";

import { planNameRow } from "../shared/definitions/planNameRow";
import { BenefitDefinitions } from "../../benefits/benefit.types";
import {
  createEnumFormatter,
  createValueFormatter,
} from "../../components/PlanEditor/utilities/enumFormatters";
import * as sharedCellSpecs from "../shared/cellSpecs/common";
import { createDisabledAwareTextFormatter } from "../shared/cellSpecs/formatters";
import {
  buildCommissionBenefits,
  buildTypeMultipleAmountsBenefit,
} from "../shared/definitions/builders";
import { employerContributionLabels } from "../shared/definitions";

// Pre-build commission definitions to override isRequired on type fields
const commissionDefs = buildCommissionBenefits();

type CellEditorParams = Partial<
  ICellEditorParams & {
    allowTyping?: boolean;
    values?: string[];
    linkedFields?: string[];
    displayMode?: string;
  }
>;

export const dentalHmoBenefitDefinitions: BenefitDefinitions = {
  currentCarrierName: {
    label: "Current Carrier",
    description: "Name of the current insurance carrier",
    cell: sharedCellSpecs.currentCarrierName,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...commissionDefs,
  currentCommissionType: {
    ...commissionDefs["currentCommissionType"],
    isRequired: true,
    editableWhen: {
      dependsOn: "policyPeriod",
      disabledValues: [
        BaseBenefitPlanPolicyPeriod.newBusiness,
        BaseBenefitPlanPolicyPeriod.alternate,
      ],
    },
  },
  requestedCommissionType: {
    ...commissionDefs["requestedCommissionType"],
    isRequired: true,
  },
  eligibilityClasses: {
    label: "Eligibility Class",
    mpConfigKey: "eligibility_class",
    description: "Benefit classes assigned to this benefit plan group",
    cell: sharedCellSpecs.eligibilityClasses,
    benefitPlanLevel: true,
    isRequired: true,
  },
  name: planNameRow,

  // Extended (non-v5) fields (CD-1659/CD-1660). The backend's
  // DentalExtendedBenefit struct is shared with dental-ppo, but only the
  // fields the DHMO sheet of the MP Data Elements and Value Standards v5
  // PropelX Presentation spreadsheet lists get a row here. One Dental MP
  // configuration drives every dental sheet, so a PPO-only field the broker
  // shows would otherwise render on DHMO too. transforms.ts still round-trips
  // the whole struct, so values stored on the others survive.
  workInProgressIncluded: {
    mpOnly: true,
    label: "Work In Progress Included",
    mpConfigKey: "work_in_progress_included",
    description: "Whether work in progress is included",
    cell: sharedCellSpecs.dentalHmoWorkInProgressIncludedDropdown,
    networkDependent: false,
  },
  openOrClosedContract: {
    mpOnly: true,
    label: "Open or Closed Contract",
    mpConfigKey: "open_or_closed_contract",
    description: "Whether the contract is open or closed",
    cell: sharedCellSpecs.dentalHmoOpenOrClosedContractDropdown,
    networkDependent: false,
  },
  // Unlike its sibling extended fields above, eligibilityHours has a
  // confirmed dropdown value list (CD-2325).
  eligibilityHours: {
    mpOnly: true,
    label: "Eligibility Hours",
    mpConfigKey: "eligibility_hours",
    description: "Minimum hours worked per week to be eligible for the plan",
    cell: sharedCellSpecs.eligibilityHoursDropdown,
    networkDependent: false,
  },
  weightedAvgOutOfPocket: {
    mpOnly: true,
    label: "Weighted Avg Out of Pocket",
    description: "Weighted average out-of-pocket cost",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  fundingType: {
    label: "Funding Type",
    description: "Funding arrangement for the benefit plan",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        allowTyping: true,
        values: Object.values(FundingTypeEnum),
        formatValue: createEnumFormatter(),
      },
      valueFormatter: createValueFormatter(),
    },
    isRequired: true,
    requiredOnCurrentCoverageOnly: true,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  adaCodeD0120: {
    label: "D0120 - Clinical Oral Evaluation - Periodic oral evaluation",
    description: "Member cost for periodic oral evaluation",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD0150: {
    label: "D0150 - Clinical Oral Evaluation - Comprehensive oral evaluation",
    description: "Member cost for comprehensive oral evaluation",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD1110: {
    label: "D1110 - Prophylaxis - Adult",
    description: "Member cost for adult prophylaxis",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD1120: {
    label: "D1120 - Prophylaxis - Child",
    description: "Member cost for child prophylaxis",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD1206: {
    label:
      "D1206 - Topical Fluoride - Topical application (w/o prophy) - child",
    description: "Member cost for topical fluoride application for child",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD1351: {
    label: "D1351 - Sealant Per Tooth Molars",
    description: "Member cost for sealant per tooth molars",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD2140: {
    label: "D2140 - Amalgam Restorations - one surface, primary/permanent",
    description: "Member cost for one surface amalgam restoration",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD2150: {
    label: "D2150 - Amalgam Restorations - two surfaces, primary/permanent",
    description: "Member cost for two surface amalgam restoration",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD2160: {
    label: "D2160 - Amalgam Restorations - three surfaces, primary/ permanent",
    description: "Member cost for three surface amalgam restoration",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD2161: {
    label: "D2161 - Amalgam Restorations - four +surfaces, primary/permanent",
    description: "Member cost for four or more surface amalgam restoration",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD2751: {
    label: "D2751 - Crowns - Porcelain fused to predominantly base metal",
    description: "Member cost for porcelain fused to base metal crown",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD2752: {
    label: "D2752 - Crowns - Porcelain fused to noble metal",
    description: "Member cost for porcelain fused to noble metal crown",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD3310: {
    label:
      "D3310 - Root Canal Therapy - Anterior (excluding final restoration)",
    description: "Member cost for anterior root canal therapy",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD3320: {
    label:
      "D3320 - Root Canal Therapy - Bicuspid (excluding final restoration)",
    description: "Member cost for bicuspid root canal therapy",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD3330: {
    label: "D3330 - Root Canal Therapy - Molar (excluding final restoration)",
    description: "Member cost for molar root canal therapy",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD3410: {
    label: "D3410 - Apicoectomy/Periradicular Surgery - Anterior",
    description: "Member cost for anterior apicoectomy",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD4210: {
    label: "D4210 - Gingivectomy - 4+ Teeth/Quad",
    description: "Member cost for gingivectomy of 4+ teeth per quadrant",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD4341: {
    label: "D4341 - Non-Surgical Periodontal - Scaling/root planing - four +",
    description: "Member cost for scaling and root planing of 4+ teeth",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD4910: {
    label: "D4910 - Non-Surgical Periodontal - Periodontal maintenance",
    description: "Member cost for periodontal maintenance",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD5110: {
    label: "D5110 - Complete Dentures - Complete denture - upper",
    description: "Member cost for complete upper denture",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD5120: {
    label: "D5120 - Complete Dentures - Complete denture - lower",
    description: "Member cost for complete lower denture",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD5213: {
    label: "D5213 - Partial Dentures - Upper w/ metal base, resin saddles, etc",
    description: "Member cost for partial upper denture with metal base",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD7111: {
    label: "D7111 - Extraction, coronal remnants - deciduous tooth",
    description:
      "Member cost for extraction of deciduous tooth coronal remnants",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD7140: {
    label: "D7140 - Extractions - Erupted tooth or exposed root",
    description: "Member cost for extraction of erupted tooth or exposed root",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD7210: {
    label: "D7210 - Surgical Extractions - extraction of tooth; erupted",
    description: "Member cost for surgical extraction of erupted tooth",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD8080: {
    label: "D8080 - Orthodontia -Treatment (Child)",
    description: "Member cost for orthodontic treatment for child",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  adaCodeD8090: {
    label: "D8090 - Orthodontia -Treatment (Adult)",
    description: "Member cost for orthodontic treatment for adult",
    cell: sharedCellSpecs.dollarAmount,
    networkDependent: false,
  },
  serviceWaitingPeriods: {
    label: "Service Waiting Periods",
    description: "Type of dental service that has a waiting period requirement",
    defaultValue: 1, // setting default value to 1 because there is an error that resets the value if set to null.
    cell: {
      ...sharedCellSpecs.serviceWaitingPeriods,
      cellEditorParams: {
        ...sharedCellSpecs.serviceWaitingPeriods.cellEditorParams,
        linkedFields: ["serviceWaitingPeriodsMonths"],
      } as CellEditorParams,
    },
    isRequired: true,
    networkDependent: false,
    invalidValues: [1],
    acceptableValues: [[]],
  },
  serviceWaitingPeriodsMonths: {
    label: "Amount",
    description:
      "Number of months a member must wait before utilizing the service (1-24)",
    defaultValue: 1,
    nested: true,
    cell: {
      ...sharedCellSpecs.serviceWaitingPeriods,
      cellEditorParams: {
        ...sharedCellSpecs.serviceWaitingPeriods.cellEditorParams,
        linkedFields: ["serviceWaitingPeriods"],
        displayMode: "months",
      } as CellEditorParams,
    },
    networkDependent: false,
  },
  annualOpenEnrollment: {
    label: "Annual Open Enrollment",
    mpConfigKey: "annual_open_enrollment",
    description: "Type of annual open enrollment available",
    cell: {
      ...sharedCellSpecs.enumDropdown,
      cellEditorParams: {
        ...sharedCellSpecs.enumDropdown.cellEditorParams,
        values: extendEnumWithNotDisclosedAndNotOffered(
          RfpAnnualOpenEnrollmentEnum,
        ),
      },
    },
    networkDependent: false,
  },
  employerContribution: {
    label: "Employer Contribution",
    description: "Defines employer vs employee contribution structure",
    cell: {
      ...sharedCellSpecs.employerContribution,
      cellEditorParams: {
        ...sharedCellSpecs.employerContribution.cellEditorParams,
        values: extendEnumWithNotDisclosed(EmployerContributionEnum),
        formatValue: createEnumFormatter(employerContributionLabels),
      },
      valueFormatter: createValueFormatter(employerContributionLabels),
    },
    isRequired: true,
    networkDependent: false,
  },
  participationRequirements: {
    label: "Participation Requirements",
    description: "Participation requirement details for the benefit plan",
    cell: sharedCellSpecs.text,
    networkDependent: false,
    benefitPlanLevel: true,
  },
  ...buildTypeMultipleAmountsBenefit({
    prefix: "rateGuarantee",
    typeLabel: "Rate Guarantee / Rate Caps",
    typeCell: sharedCellSpecs.enumDropdown,
    typeEnumValues: Object.keys(RateGuaranteeTypeEnum),
    typeDescription:
      "Time frame in which rates are guaranteed to stay the same",
    amountFields: [
      {
        suffix: "Amount",
        label: "Amount",
        cell: sharedCellSpecs.numberField,
        description: "Number of months or years the rate is guaranteed",
        defaultValue: 1,
        disabledValues: [
          RateGuaranteeTypeEnum.notIncluded,
          RateGuaranteeTypeEnum.notOffered,
        ],
      },
      {
        suffix: "Cap",
        label: "Cap",
        cell: {
          ...sharedCellSpecs.text,
          cellEditorParams: {
            maxLength: 1024,
          },
          validation: {
            ...sharedCellSpecs.text.validation,
            maxLength: 1024,
          },
          valueFormatter: createDisabledAwareTextFormatter(),
        },
        description: "Rate cap details following the rate guarantee period",
        disabledValues: [
          RateGuaranteeTypeEnum.notIncluded,
          RateGuaranteeTypeEnum.notOffered,
        ],
      },
    ],
    disabledValues: ["notIncluded", "notOffered"],
  }),
} as const;
